import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, Button } from "@tabipla/ui";
import {
  Plus,
  Trash2,
  Utensils,
  Train,
  Hotel,
  Compass,
  ShoppingBag,
  MoreHorizontal,
} from "lucide-react";
import type { ExpenseResponseDto, ExpenseCategory } from "@tabipla/shared";

interface ExpenseListProps {
  expenses: ExpenseResponseDto[];
  onAddClick: () => void;
  onDeleteClick: (expenseId: string) => void;
  canEdit: boolean;
}

const categoryIcons: Record<ExpenseCategory, React.ReactNode> = {
  food: <Utensils className="h-4 w-4" />,
  transport: <Train className="h-4 w-4" />,
  accommodation: <Hotel className="h-4 w-4" />,
  activity: <Compass className="h-4 w-4" />,
  shopping: <ShoppingBag className="h-4 w-4" />,
  other: <MoreHorizontal className="h-4 w-4" />,
};

const categoryColors: Record<ExpenseCategory, string> = {
  food: "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300",
  transport: "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300",
  accommodation: "bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300",
  activity: "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300",
  shopping: "bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-300",
  other: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

export function ExpenseList({ expenses, onAddClick, onDeleteClick, canEdit }: ExpenseListProps) {
  const { t } = useTranslation();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ja-JP", {
      month: "short",
      day: "numeric",
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">{t("expenses.title")}</CardTitle>
        {canEdit && (
          <Button size="sm" onClick={onAddClick}>
            <Plus className="mr-2 h-4 w-4" />
            {t("expenses.addExpense")}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {expenses.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t("expenses.noExpenses")}
          </p>
        ) : (
          <div className="space-y-2">
            {expenses.map((expense) => (
              <div
                key={expense.id}
                className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-accent"
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full ${categoryColors[expense.category]}`}
                  >
                    {categoryIcons[expense.category]}
                  </div>
                  <div>
                    <p className="font-medium">{expense.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {expense.paidByName} · {formatDate(expense.date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{formatAmount(expense.amount)}</span>
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onDeleteClick(expense.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
